import {
  AlertFilled,
  FileTextFilled,
  FireFilled,
  HeartFilled,
  InfoCircleFilled,
  MedicineBoxFilled,
  SmileFilled,
} from '@ant-design/icons';
import { DatePicker } from 'antd';
import CalendarTabs from 'components/cards/CalendarTabs';
import ModuleCard from 'components/cards/ModuleCard';
import { calculateCalendarChartHeight, FertilityDot } from 'components/charts/CalendarChart';
import LinearCalendarChart from 'components/charts/LinearCalendarChart';
import PeriodChart from 'components/charts/PeriodChart';
import SexualActivityChart from 'components/charts/SexualActivityChart';
import SexualActivityChartLegend from 'components/charts/SexualActivityChartLegend';
import SymptomsChart from 'components/charts/SymptomsChart';
import SymptomsChartLegend from 'components/charts/SymptomsChartLegend';
import TemperatureChart from 'components/charts/TemperatureChart';
import {
  VAGINAL_DISCHARGE_COLORS,
  VAGINAL_DISCHARGE_ICON_MAP,
  VAGINAL_DISCHARGE_SHAPE_MAP,
} from 'components/charts/VaginalDischargesColors';
import dayjs from 'dayjs';
import usePreferences from 'hooks/usePreferences';
import useShouldAskForPeriod from 'hooks/useShouldAskForPeriod';
import useSymptomTrackingData, { getOvulationAndFertilityWindow } from 'hooks/useSymptomTrackingData';
import { locale } from 'locales/i18nManager';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Button } from 'components/ui';
import { colors, palette } from 'design';

// height reserved, below each chart's own height, for the gap to the next title (24px title height + 32px margin + 4px title-to-chart gap)
const TITLE_ROW_GAP = 24 + 32 + 4;

const DropIcon = ({ color }: { color: string }) => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 14 21"
    fill={color}
    style={{ verticalAlign: '-0.125em', display: 'inline-block' }}
  >
    <path d="M14 14C14 17.866 10.866 21 7 21C3.13401 21 0 17.866 0 14C0 10.134 3.13401 0 7 0C10.866 0 14 10.134 14 14Z" />
  </svg>
);

const GRAPH_ICONS: Record<string, React.ReactNode> = {
  generalState: <SmileFilled style={{ color: colors.dataViz.veryGood }} />,
  period: <DropIcon color={colors.dataViz.veryBad} />,
  pain: <AlertFilled style={{ color: colors.dataViz.bad }} />,
  sexualActivity: <HeartFilled style={{ color: colors.brand.highlight }} />,
  bodyTemperature: <FireFilled style={{ color: colors.brand.highlightStrong }} />,
  vaginalDischarges: <DropIcon color={palette.teal[300]} />,
  symptoms: <MedicineBoxFilled style={{ color: colors.dataViz.average }} />,
  notes: <FileTextFilled style={{ color: colors.text.tertiary }} />,
};

const useStyles = createUseStyles({
  historyContainer: {
    marginTop: 12,
    position: 'relative',
  },
  historyRangeFooter: {
    maxWidth: 264,
    lineHeight: '1.5em',
    '& svg': {
      marginRight: 4,
    },
  },
  // The formatted label ("8 juin - 8 sept.") sizes the picker to its content
  // with no cap, so on a narrow phone it and the "Historique" title together
  // overflow `moduleCardHeader`'s row instead of fitting beside it.
  historyRangePicker: {
    maxWidth: '55vw',
    '& .ant-picker-input input': {
      fontSize: 13,
      textOverflow: 'ellipsis',
    },
  },
  historyGraphTitlesContainer: {
    // this container is overlaid with the history graphs container. Titles don't share a single fixed gap since
    // not all graphs have the same height (eg. the sexual activity graph is more compact) - each title gets an
    // explicit marginBottom matching its own graph's height instead. Each title's legend (if any) is rendered
    // right after it, so it lands directly below its own graph rather than at the bottom of the column - the
    // matching graph row gets its own height bumped by the legend's measured height to stay aligned, see
    // legendHeights/graphRows below.
    display: 'flex',
    flexDirection: 'column',
    '& > h3': {
      margin: '32px 0px 0px',
      height: 24,
    },
  },
  historyGraphsContainer: {
    // this container is overlaid on top of the history titles container
    // it allows smooth scrolling of all graphs together
    overflowX: 'auto',
    overflowY: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: TITLE_ROW_GAP, // first title height + gap
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    scrollbarWidth: 'none',
  },
  graphTitle: {
    margin: '16px 0px 8px',
    color: colors.text.tertiary,
  },
  historyGraphsFakeContainer: {
    overflow: 'hidden',
  },
  pleaseFillYourDataOverlay: {
    color: colors.text.disabled,
    fontSize: 14,
    textAlign: 'center',
    padding: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,

    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',

    backdropFilter: 'blur(2px)',

    '& p': {
      margin: 0,
    },
  },
  pleaseFillYourDataOverlayText: {
    background: `radial-gradient(closest-side, rgba(255,255,255,1), rgba(255,255,255,0))`,
    textAlign: 'center',
    padding: '48px 36px',
    borderRadius: 8,
    color: colors.text.primary,
  },
  noDataText: {
    color: colors.text.tertiary,
    textAlign: 'center',
  },
  errorFallback: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    textAlign: 'center',
    color: colors.dataViz.bad,
    padding: '0px 32px',
  },
  controlsContainer: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
    '& .ant-select': {
      flex: 1,
    },
    '& .ant-picker': {
      flex: 1,
    },
  },
  graphSelectorWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginBottom: 4,
    padding: '4px 0 0px',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: 56,
      background: 'linear-gradient(to left, white 0%, transparent 100%)',
      pointerEvents: 'none',
      zIndex: 1,
    },
  },
  graphSelectorContainer: {
    display: 'flex',
    overflowX: 'auto',
    overflowY: 'visible',
    gap: 8,
    padding: '3px 56px 3px 3px',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '& button': {
      whiteSpace: 'nowrap',
      flexShrink: 0,
    },
    '& button[data-selected="false"]': {
      color: colors.text.tertiary,
    },
    '& button[data-selected="false"] .anticon': {
      color: colors.text.tertiary,
    },
    '& button[data-selected="false"] svg': {
      fill: colors.text.tertiary,
    },
  },
});

export interface HistoryModuleProps {
  enoughDataForHistory: boolean;
}

const HistoryModule: React.FC<HistoryModuleProps> = ({ enoughDataForHistory }) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const historyGraphsRef = useRef<HTMLDivElement>(null);
  const today = dayjs();

  // History date management
  const [historyEndDate, setHistoryEndDate] = useState<dayjs.Dayjs>(dayjs());
  const historyStartDate = historyEndDate.subtract(3, 'month');

  // Period tracking preferences
  const { isPeriodTrackingEnabled } = useShouldAskForPeriod();

  // Graph visibility preferences
  const { value: visibleGraphsPreference, set: setVisibleGraphsPreference } = usePreferences(
    Keys.TRACKING_HISTORY_VISIBLE_GRAPHS,
  );

  // Default to only period graph if no preference is set
  const defaultVisibleGraphs = isPeriodTrackingEnabled ? ['period'] : ['generalState', 'pain', 'symptoms'];

  // usePreferences only reflects a write after an async Preferences.set() -> get() round-trip, so rapid
  // sequential toggles would otherwise compute their selection from a stale snapshot and silently undo each
  // other. Keep a locally, synchronously updated copy that's the source of truth for the UI once loaded, and
  // only seed it from the persisted preference once (on initial load). The onClick handler uses the functional
  // setState form so it always reads the latest selection rather than a stale closed-over value.
  const [optimisticVisibleGraphs, setOptimisticVisibleGraphs] = useState<string[] | null>(null);
  useEffect(() => {
    if (optimisticVisibleGraphs === null && Array.isArray(visibleGraphsPreference)) {
      setOptimisticVisibleGraphs(visibleGraphsPreference);
    }
  }, [visibleGraphsPreference, optimisticVisibleGraphs]);
  const visibleGraphs = optimisticVisibleGraphs ?? defaultVisibleGraphs;

  // Symptom tracking data
  const { getSpecificDailyStatus, getStatusValueForDisplayedDays, cycleHistory, cycleStats } = useSymptomTrackingData();

  // matches the actual height of each chart, so that titles (which sit in their own, separate column) stay
  // aligned with their corresponding graph - see the historyGraphTitlesContainer comment above
  const standardRowHeight = calculateCalendarChartHeight({ showMonthLabels: true });
  const bodyTemperatureRowHeight = calculateCalendarChartHeight({ showMonthLabels: true, yRangeOverride: 7.5 });
  const sexualActivityRowHeight = calculateCalendarChartHeight({
    hasBottomDots: true,
    showMonthLabels: true,
    yRangeOverride: 2,
  });

  // Fertility window dots, displayed below day numbers on the sexual activity chart
  const fertilityDots = useMemo(() => {
    const dots: FertilityDot[] = [];
    cycleHistory.forEach(cycle => {
      const { ovulationDate, fertileStartDate, fertileEndDate, isFertilityWindowPredictionReliable } =
        getOvulationAndFertilityWindow(cycle, cycleStats);
      if (!isFertilityWindowPredictionReliable) return;
      let day = fertileStartDate;
      while (day.isSameOrBefore(fertileEndDate, 'day')) {
        dots.push({
          date: day,
          type: day.isSame(ovulationDate, 'day') ? 'ovulation' : 'fertile',
          predicted: !cycle.completed,
        });
        day = day.add(1, 'day');
      }
    });
    return dots;
  }, [cycleHistory, cycleStats]);

  // Calculate displayed days
  const displayedDays = useMemo(() => {
    return Array.from({ length: historyEndDate.diff(historyStartDate, 'day') + 1 }, (_, i) =>
      historyStartDate.add(i, 'day').set('hour', 0).set('minute', 0).set('second', 0).set('millisecond', 0),
    );
  }, [historyStartDate, historyEndDate]);

  // Legends (eg. symptoms, vaginal discharges) have content-dependent heights that can't be computed upfront like
  // chart heights can, so we measure them once rendered and use that to keep the graphs column (see graphRows
  // below) aligned with the titles column.
  const [legendHeights, setLegendHeights] = useState<Record<string, number>>({});
  const legendObservers = useRef<Record<string, ResizeObserver>>({});
  const observeLegend = useCallback(
    (key: string) => (el: HTMLDivElement | null) => {
      legendObservers.current[key]?.disconnect();
      delete legendObservers.current[key];
      if (!el) return;
      const observer = new ResizeObserver(entries => {
        const height = entries[0]?.contentRect.height ?? 0;
        setLegendHeights(prev => (prev[key] === height ? prev : { ...prev, [key]: height }));
      });
      observer.observe(el);
      legendObservers.current[key] = observer;
    },
    [],
  );
  useEffect(() => {
    const observers = legendObservers.current;
    return () => Object.values(observers).forEach(observer => observer.disconnect());
  }, []);

  // One entry per visible graph, in display order, holding everything needed to render its title, optional legend
  // and chart in sync across the two overlaid columns described in historyGraphTitlesContainer above.
  const graphRows = useMemo(() => {
    const rows: {
      key: string;
      testId: string;
      title: string;
      icon: React.ReactNode;
      rowHeight: number;
      legend?: React.ReactNode;
      graph: React.ReactNode;
    }[] = [];

    if (visibleGraphs.includes('generalState')) {
      rows.push({
        key: 'generalState',
        testId: 'general-state-graph',
        title: t('symptomTracking.graphs.generalState'),
        icon: GRAPH_ICONS.generalState,
        rowHeight: standardRowHeight,
        graph: (
          <LinearCalendarChart
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('general_state', displayedDays)}
            range={[0, 4]}
            showMonthLabels
          />
        ),
      });
    }
    if (isPeriodTrackingEnabled && visibleGraphs.includes('period')) {
      rows.push({
        key: 'period',
        testId: 'period-graph',
        title: t('symptomTracking.graphs.menstruation'),
        icon: GRAPH_ICONS.period,
        rowHeight: standardRowHeight,
        graph: (
          <PeriodChart
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('menstruation_amount', displayedDays)}
            showMonthLabels
          />
        ),
      });
    }
    if (visibleGraphs.includes('pain')) {
      rows.push({
        key: 'pain',
        testId: 'pain-graph',
        title: t('symptomTracking.graphs.pain'),
        icon: GRAPH_ICONS.pain,
        rowHeight: standardRowHeight,
        graph: (
          <LinearCalendarChart
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('pain_level', displayedDays)}
            range={[0, 4]}
            reverseColors
            showMonthLabels
          />
        ),
      });
    }
    if (visibleGraphs.includes('sexualActivity')) {
      rows.push({
        key: 'sexualActivity',
        testId: 'sexual-activity-graph',
        title: t('symptomTracking.graphs.sexualActivity'),
        icon: GRAPH_ICONS.sexualActivity,
        rowHeight: sexualActivityRowHeight,
        legend: <SexualActivityChartLegend />,
        graph: (
          <SexualActivityChart
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('sexual_activity', displayedDays, null)}
            fertilityDots={fertilityDots}
            showMonthLabels
          />
        ),
      });
    }
    if (visibleGraphs.includes('bodyTemperature')) {
      rows.push({
        key: 'bodyTemperature',
        testId: 'body-temperature-graph',
        title: t('symptomTracking.graphs.bodyTemperature'),
        icon: GRAPH_ICONS.bodyTemperature,
        rowHeight: bodyTemperatureRowHeight,
        graph: (
          <TemperatureChart
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('body_temperature', displayedDays)}
            showMonthLabels
          />
        ),
      });
    }
    if (visibleGraphs.includes('vaginalDischarges')) {
      rows.push({
        key: 'vaginalDischarges',
        testId: 'vaginal-discharges-graph',
        title: t('symptomTracking.graphs.vaginalDischarges'),
        icon: GRAPH_ICONS.vaginalDischarges,
        rowHeight: standardRowHeight,
        legend: (
          <SymptomsChartLegend
            dayValues={getStatusValueForDisplayedDays('vaginal_discharges', displayedDays, [])}
            translationKeyPrefix="symptomTracking.survey.vaginalDischarges"
            colorMap={VAGINAL_DISCHARGE_COLORS}
            iconMap={VAGINAL_DISCHARGE_ICON_MAP}
          />
        ),
        graph: (
          <SymptomsChart
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('vaginal_discharges', displayedDays, [])}
            showMonthLabels
            colorMap={VAGINAL_DISCHARGE_COLORS}
            shapeMap={VAGINAL_DISCHARGE_SHAPE_MAP}
          />
        ),
      });
    }
    if (visibleGraphs.includes('symptoms')) {
      rows.push({
        key: 'symptoms',
        testId: 'symptoms-graph',
        title: t('symptomTracking.graphs.symptoms'),
        icon: GRAPH_ICONS.symptoms,
        rowHeight: standardRowHeight,
        legend: <SymptomsChartLegend dayValues={getStatusValueForDisplayedDays('symptoms', displayedDays, [])} />,
        graph: (
          <SymptomsChart
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('symptoms', displayedDays, [])}
            showMonthLabels
          />
        ),
      });
    }

    return rows;
  }, [
    visibleGraphs,
    isPeriodTrackingEnabled,
    t,
    standardRowHeight,
    bodyTemperatureRowHeight,
    sexualActivityRowHeight,
    displayedDays,
    getStatusValueForDisplayedDays,
    fertilityDots,
  ]);

  // Graph options for the multiselect
  const graphOptions = useMemo(() => {
    const options = [
      { label: t('symptomTracking.graphs.generalState'), value: 'generalState', icon: GRAPH_ICONS.generalState },
      { label: t('symptomTracking.graphs.pain'), value: 'pain', icon: GRAPH_ICONS.pain },
      { label: t('symptomTracking.graphs.sexualActivity'), value: 'sexualActivity', icon: GRAPH_ICONS.sexualActivity },
      {
        label: t('symptomTracking.graphs.bodyTemperature'),
        value: 'bodyTemperature',
        icon: GRAPH_ICONS.bodyTemperature,
      },
      {
        label: t('symptomTracking.graphs.vaginalDischarges'),
        value: 'vaginalDischarges',
        icon: GRAPH_ICONS.vaginalDischarges,
      },
      { label: t('symptomTracking.graphs.symptoms'), value: 'symptoms', icon: GRAPH_ICONS.symptoms },
    ];

    if (isPeriodTrackingEnabled) {
      options.splice(1, 0, {
        label: t('symptomTracking.graphs.menstruation'),
        value: 'period',
        icon: GRAPH_ICONS.period,
      });
    }

    return options;
  }, [t, isPeriodTrackingEnabled]);

  useEffect(() => {
    if (historyGraphsRef.current) {
      historyGraphsRef.current.scrollLeft = historyGraphsRef.current.scrollWidth;
    }
  }, [historyGraphsRef.current, enoughDataForHistory]);

  const ErrorFallback = (props: FallbackProps) => {
    void props.error;
    void props.resetErrorBoundary;
    return (
      <div className={styles.errorFallback}>
        <p>{t('symptomTracking.error.title')}</p>
      </div>
    );
  };

  const daysWithNotes = (displayedDays.map(getSpecificDailyStatus).filter((d: any) => d !== undefined) || []).filter(
    status => status.comment || status.extra_symptoms,
  );

  return (
    <ModuleCard
      title={t('symptomTracking.modules.historyTitle')}
      data-testid="history-module"
      topRightContent={
        enoughDataForHistory ? (
          <DatePicker
            className={styles.historyRangePicker}
            allowClear={false}
            value={historyEndDate}
            variant="filled"
            onChange={value => {
              if (!value) return;
              setHistoryEndDate(value.set('hour', 0).set('minute', 0).set('second', 0).set('millisecond', 0));
              posthog.capture('tracking.history_range_changed');
            }}
            format={value =>
              `${historyStartDate.toDate().toLocaleDateString(locale, { month: 'short', day: 'numeric' })} - ${value.toDate().toLocaleDateString(locale, { month: 'short', day: 'numeric' })}`
            }
            renderExtraFooter={() => {
              return (
                <p className={styles.historyRangeFooter}>
                  <InfoCircleFilled /> {t('symptomTracking.history.historyDescription')}
                </p>
              );
            }}
          />
        ) : null
      }
    >
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(error, errorInfo) => console.error('Error in History', error, errorInfo)}
      >
        <div className={styles.historyContainer}>
          {enoughDataForHistory ? (
            <>
              <div className={styles.graphSelectorWrapper}>
                <div className={styles.graphSelectorContainer}>
                  {graphOptions.map(option => (
                    <Button
                      key={option.value}
                      selected={visibleGraphs.includes(option.value)}
                      onClick={e => {
                        e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                        setOptimisticVisibleGraphs(prev => {
                          const currentSelection = prev ?? defaultVisibleGraphs;
                          const isCurrentlySelected = currentSelection.includes(option.value);
                          const newSelection = isCurrentlySelected
                            ? currentSelection.filter(graph => graph !== option.value)
                            : [...currentSelection, option.value];
                          if (newSelection.length === 0) return currentSelection;
                          setVisibleGraphsPreference(JSON.stringify(newSelection));
                          posthog.capture('tracking.history_graphs_selection_changed', {
                            selectedGraphs: newSelection,
                          });
                          posthog.capture(
                            isCurrentlySelected ? 'tracking.history_graph_hidden' : 'tracking.history_graph_shown',
                            { selectedGraph: option.value },
                          );
                          return newSelection;
                        });
                      }}
                    >
                      {option.icon} {option.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className={styles.historyContainer}>
                <div className={styles.historyGraphTitlesContainer}>
                  {graphRows.map(row => (
                    <Fragment key={row.key}>
                      <h3
                        className={styles.graphTitle}
                        data-testid={row.testId}
                        style={{ marginBottom: row.rowHeight + 4 }}
                      >
                        {row.icon} {row.title}
                      </h3>
                      {row.legend && (
                        <div ref={observeLegend(row.key)} data-testid={`${row.testId}-legend`}>
                          {row.legend}
                        </div>
                      )}
                    </Fragment>
                  ))}
                </div>
                <div className={styles.historyGraphsContainer} ref={historyGraphsRef}>
                  {graphRows.map((row, index) => (
                    <div
                      key={row.key}
                      style={
                        index < graphRows.length - 1
                          ? { marginBottom: TITLE_ROW_GAP + (legendHeights[row.key] ?? 0) }
                          : undefined
                      }
                    >
                      {row.graph}
                    </div>
                  ))}
                </div>
                {daysWithNotes.length > 0 && (
                  <>
                    <h3 className={styles.graphTitle}>
                      {GRAPH_ICONS.notes} {t('symptomTracking.graphs.notes')}
                    </h3>
                    <CalendarTabs daysWithNotes={daysWithNotes} />
                  </>
                )}
              </div>
            </>
          ) : (
            <div className={styles.historyGraphsFakeContainer}>
              <div style={{ position: 'relative' }}>
                <LinearCalendarChart
                  dayLabels={Array.from({ length: 40 }, (_, i) => today.subtract(i, 'days')).reverse()}
                  dayValues={Array(2).fill([0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 1, 2, 3, 1, 0, 0, 1, 0, 0, 0]).flat()}
                  range={[0, 4]}
                  reverseColors
                  showMonthLabels
                />
                <div className={styles.pleaseFillYourDataOverlay}>
                  <div className={styles.pleaseFillYourDataOverlayText}>
                    <p className={styles.noDataText}>Pas de données</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </ErrorBoundary>
    </ModuleCard>
  );
};

export default HistoryModule;
